import { createTheme, alpha } from '@mui/material/styles'
import { grey } from '@mui/material/colors'
import textTheme from './textTheme'

const buttonFontSize = '20px'
const smallButtonFontSize = '16px'
const xsButtonFontSize = '14px'

export const headerHeight = '56px'

export const indexPageMaxContentWidth = '1200px'
export const maxContentWidth = '1600px'
export const longerMaxContentWidth = '1924px'

let theme = createTheme({
  palette: {
    primary: {
      light: '#649100',
      main: '#75aa00',
      dark: '#89C10D',
      bgPlus1: '#F0FBD9',
      bgPlus2: '#F8FFE8',
      minus2: '#C3DB92',
      contrastText: '#fff',
      mainPlus2: '#498300',
    },
    secondary: {
      light: '#F9450F',
      main: '#DD3703',
      dark: '#C52600',
      contrastText: '#fff',
      minus2: '#FF683A',
      bg: '#FCE8E6',
    },
    error: {
      light: '#FB5133',
      main: '#F23919',
      dark: '#E62200',
      contrastText: '#fff',
      bgPlus1: '#FFD8D0',
    },
    neutral: {
      main: '#383838',
      light: '#5A5A5A',
      dark: '#000000',
    },
    text: {
      primary: '#383838',
      secondary: '#9B9B9B',
      disabled: '#BDBDBD',
      hint: '#D9D9D9',
      minus1: '#383838',
      minus2: '#5A5A5A',
      dark: '#000000',
    },
    background: {
      default: '#FFFFFF',
      gray: '#F0F0F0',
      line: '#D4D4D4',
      disabled: '#DEDEDE',
    },
    grey: {
      main: '#F0F0F0',
      dark: grey[400],
      secondary: '#EBEBEB',
      third: '#F6F6F6',
    },
    orange: {
      main: '#EE8613',
    },
    green: {
      main: '#4CC764',
      secondary: '#97C440',
    },
    blue: {
      main: '#1877F2',
    },
    white: {
      light: '#FFFFFF',
      main: '#FFFFFF',
      dark: '#FFFFFF',
      contrastText: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'San Francisco',
      'system-ui',
      'sans-serif',
    ].join(','),
    h6: {
      lineHeight: '1.2',
      fontWeight: 400,
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 700,
    },
    h4: {
      fontSize: '1.75rem',
    },
    h3: {
      fontSize: '2.25rem',
      fontWeight: 700,
    },
    h2: {
      fontSize: '3rem',
      fontWeight: 700,
    },
    subtitle2: {
      fontSize: '1.25rem',
      fontWeight: 700,
    },
    subtitle3: {
      fontSize: '1.125rem',
      fontWeight: 700,
    },
    body1: {
      lineHeight: '1.2',
    },
    body2: {
      fontSize: '0.875rem',
    },
    body3: {
      fontSize: '0.75rem',
    },
    button: {
      lineHeight: '1.2',
    },
    label: {
      fontSize: '1rem',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: '1.2',
    },
    ...textTheme,
  },
  spacing: 4,
  breakpoints: {
    values: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1600,
      maxLimit: 1924,
    },
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '15px',
        },
        text: {
          padding: 0,
          fontSize: '14px',
          fontWeight: 400,
          '&:hover': {
            boxShadow: 'none !important',
            backgroundColor: 'transparent !important',
          },
          '&:focus': {
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
          },
        },
        contained: {
          padding: '15px',
          fontSize: buttonFontSize,
          boxShadow: 'none',
          '&.Mui-disabled': {
            backgroundColor: '#DEDEDE',
            color: '#BDBDBD',
          },
          '&:hover': {
            boxShadow: 'none !important',
          },
          '&:focus': {
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
          },
        },
        outlined: {
          padding: '15px',
          fontSize: buttonFontSize,
        },
        containedSizeSmall: {
          padding: '12px 15px',
          fontSize: smallButtonFontSize,
          fontWeight: 500,
        },
        containedSizeXs: {
          padding: '8px 12px',
          fontSize: xsButtonFontSize,
          fontWeight: 500,
        },
        outlinedSizeSmall: {
          padding: '12px 15px',
          fontSize: smallButtonFontSize,
          fontWeight: 500,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '20px',
        },
        input: {
          paddingTop: '16px',
          paddingBottom: '16px',
          backgroundColor: 'white',
        },
        inputSizeSmall: {
          fontSize: smallButtonFontSize,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#D4D4D4',
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        ol: {
          flexWrap: 'nowrap',
          overflow: 'hidden',
        },
        li: {
          '&:last-child': {
            overflow: 'hidden',
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        // Typography 預設會用 <p>，若在 <p> 包覆其他 <tag> 就會有 warning，所以改用 <div>
        component: 'div',
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        sx: {
          fontSize: '20px',
        },
      },
    },
  },
})

theme = createTheme(theme, {
  components: {
    MuiButton: {
      variants: [
        {
          props: { color: 'grey', variant: 'text' },
          style: {
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: alpha(
                theme.palette.text.primary,
                theme.palette.action.hoverOpacity
              ),
            },
          },
        },
        {
          props: { color: 'grey', variant: 'outlined' },
          style: {
            color: theme.palette.text.primary,
            borderColor: theme.palette.background.line,
            '&:hover': {
              borderColor: theme.palette.background.line,
              backgroundColor: alpha(
                theme.palette.text.primary,
                theme.palette.action.hoverOpacity
              ),
            },
          },
        },
      ],
    },
  },
})

export default theme
